import FormField from '@components/Common/FormField';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Typography } from '@mui/material';
import React from 'react';
import { SECTION_LABELS, sectionKind } from './sections';
import StructuredRevisionChat from './StructuredRevisionChat';
import FieldEditor from './structured/FieldEditor';
import { specFor } from './structured/fieldMap';
import JsonField from './structured/JsonField';

interface SectionEditorProps {
  name: string;
  value: unknown;
  required: boolean;
  onChange: (name: string, next: unknown) => void;
  onRemove?: (name: string) => void;
  /** Which template the guide uses; picks the field map. Absent means "infer from the value". */
  pageType?: string;
  /**
   * When present, the section offers "Ask AI to edit this section": a chat scoped to this one
   * top-level key. It needs the whole envelope (the edit returns the whole envelope) and a writer
   * for the complete result. Absent on surfaces where AI editing does not apply.
   */
  revise?: {
    contentJson: string;
    pageType: string;
    onRevised: (contentJson: string) => void;
  };
}

/**
 * One section of the guide body.
 *
 * A key the field map knows for this page type gets its typed editor: steps with images, a table
 * grid, chip groups, charts, figures. Anything else keeps the old inference from what the section
 * holds (a paragraph gets a textarea, a list of strings one field per item) and falls back to JSON
 * for that section alone rather than for the whole document. Nothing the map does not know about
 * is hidden.
 */
const SectionEditor: React.FC<SectionEditorProps> = ({
  name,
  value,
  required,
  onChange,
  onRemove,
  pageType = '',
  revise,
}) => {
  const spec = specFor(pageType, name);
  const kind = sectionKind(value);
  const label = spec?.label ?? SECTION_LABELS[name] ?? name;
  const [askAi, setAskAi] = React.useState(false);

  return (
    <Box className="ge-section" data-section={name}>
      <Box className="ge-section__head">
        <Typography component="h3" className="ge-section__title">
          {label}
        </Typography>
        {revise && (
          <IconButton
            size="small"
            aria-label={`Ask AI to edit ${label}`}
            title={`Ask AI to edit ${label}`}
            aria-pressed={askAi}
            onClick={() => setAskAi((v) => !v)}
          >
            <AutoFixHighOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {required ? (
          <span className="ge-section__req">Required</span>
        ) : (
          onRemove && (
            <IconButton
              size="small"
              onClick={() => onRemove(name)}
              aria-label={`Remove ${label}`}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )
        )}
      </Box>

      {revise && askAi && (
        <Box className="ge-section__ai">
          <StructuredRevisionChat
            contentJson={revise.contentJson}
            pageType={revise.pageType}
            scope={[name]}
            scopeLabel={label}
            onRevised={revise.onRevised}
          />
        </Box>
      )}

      {spec ? (
        <FieldEditor
          spec={spec}
          value={value}
          path={name}
          hideLabel
          onChange={(next) => onChange(name, next)}
        />
      ) : (
        <>
          {kind === 'text' && (
            <FormField
              required={false}
              multiline
              name={name}
              label=""
              value={value as string}
              placeholder={`${label} copy`}
              textFieldProps={{ rows: undefined, minRows: 3 }}
              onChange={(_n, next) => onChange(name, String(next))}
            />
          )}

          {kind === 'list' && (
            <Box className="ge-list">
              {(value as string[]).map((item, i) => (
                <Box key={i} className="ge-list__row">
                  <FormField
                    required={false}
                    multiline
                    name={`${name}-${i}`}
                    label=""
                    value={item}
                    onChange={(_n, edited) => {
                      const next = [...(value as string[])];
                      next[i] = String(edited);
                      onChange(name, next);
                    }}
                  />
                  <IconButton
                    size="small"
                    aria-label={`Remove item ${i + 1}`}
                    onClick={() => onChange(name, (value as string[]).filter((_, j) => j !== i))}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onChange(name, [...(value as string[]), ''])}
              >
                Add item
              </Button>
            </Box>
          )}

          {kind === 'json' && (
            <JsonField name={name} value={value} onChange={(next) => onChange(name, next)} />
          )}
        </>
      )}
    </Box>
  );
};

export default SectionEditor;
